import { useState, useEffect, useRef } from 'react';

interface OptionItem {
  id: string;
  value: string;
}

interface Props {
  options: string[];
  onChange: (options: string[]) => void;
  label?: string;
}

let optIdCounter = 0;
function nextOptId(): string {
  return `opt-${++optIdCounter}`;
}

function toItems(values: string[]): OptionItem[] {
  return values.map(v => ({ id: nextOptId(), value: v }));
}

export function OptionsEditor({ options, onChange, label = 'Opciones *' }: Props) {
  const [localOptions, setLocalOptions] = useState<OptionItem[]>(() =>
    options.length > 0 ? toItems(options) : [{ id: nextOptId(), value: '' }]
  );
  const prevOptionsRef = useRef(options);

  useEffect(() => {
    if (prevOptionsRef.current === options) return;
  
    prevOptionsRef.current = options;
  
    setLocalOptions((prev) => {
      // Mantener IDs existentes
      return options.map((value, index) => ({
        id: prev[index]?.id || nextOptId(),
        value,
      }));
    });
  }, [options]);

  const updateOptions = (items: OptionItem[]) => {
    const filtered = items.filter((o) => o.value.trim().length > 0);
    if (filtered.length > 0) {
      setLocalOptions(filtered);
      onChange(filtered.map(o => o.value));
    } else {
      setLocalOptions([{ id: nextOptId(), value: '' }]);
      onChange([]);
    }
  };

  const handleOptionChange = (id: string, value: string) => {
    const updated = localOptions.map(o => o.id === id ? { ...o, value } : o);
    updateOptions(updated);
  };

  const handleAddOption = () => {
    setLocalOptions(prev => [...prev, { id: nextOptId(), value: '' }]);
  };

  const handleRemoveOption = (id: string) => {
    if (localOptions.length === 1) {
      setLocalOptions([{ id: nextOptId(), value: '' }]);
      onChange([]);
      return;
    }
    updateOptions(localOptions.filter(o => o.id !== id));
  };

  return (
    <div>
      <label className="block text-sm font-medium text-foreground mb-2">
        {label}
      </label>
      <div className="space-y-2">
        {localOptions.map((option) => (
          <div key={option.id} className="flex gap-2 items-center">
            <input
              type="text"
              value={option.value}
              onChange={(e) => handleOptionChange(option.id, e.target.value)}
              placeholder="Escriba una opción..."
              className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
            />
            <button
              type="button"
              onClick={() => handleRemoveOption(option.id)}
              className="p-2 rounded-md border border-destructive bg-destructive/10 text-destructive hover:bg-destructive/20 transition-colors"
              title="Eliminar opción"
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={handleAddOption}
          className="w-full inline-flex items-center justify-center rounded-md border border-dashed border-input bg-background px-3 py-2 text-sm text-muted-foreground hover:bg-muted transition-colors"
        >
          + Agregar opción
        </button>
      </div>
      {options.length === 0 && (
        <p className="text-xs text-destructive mt-1">
          Debe agregar al menos una opción
        </p>
      )}
    </div>
  );
}

